/**
 * The Tenants table's column model, in rem, shared by the header, the `<colgroup>` and the
 * row so they cannot drift apart.
 *
 * Each column is either fixed (`width`) or flexible (`flex: true`, `minWidth`, no `width`).
 * The table is `table-fixed`: a fixed column is exactly its width, the flexible columns (name,
 * owner) get no `<col>` width and so share whatever the fixed ones leave, equally. Text longer
 * than its column is cut inside the cell (full value on `title`) instead of widening the
 * table. The narrowest layout (fixed widths + flex minimums = 33.5 + 17 = 50.5rem) has to fit
 * the table area of a 1280px viewport: 1280 - 280 (sidebar) - 64 (page padding) - ~15
 * (scrollbar) = ~921px = ~57.5rem. There name and owner get ~12rem each; at 1440 (~67.5rem)
 * ~17rem each. The browser run that measured the previous
 * auto-layout table found its scroller 228-261px wider than its card at 1280 (68-101px at
 * 1440), which hid Suspend / Activate / Delete behind a horizontal scrollbar.
 *
 * Text widths (12px Figtree, measured on the longest labels in EN / FR / ES): a status label
 * runs ~5.7px per character, an upper-case tracked header ~8.2px per character. The
 * widest status is FR "Paiement en retard", 18 chars = ~102px; inside its badge (2 x 10px
 * padding + 2px border) and the cell (2 x 12px padding) that is 102 + 22 + 24 = 148px, so
 * `status` gets 9.5rem = 152px and no label is ever cut (the badge may still wrap onto a
 * second line, never an ellipsis). The widest header is ES "PROPIETARIO", 11 chars =
 * ~90px + 24px = 114px, inside `owner` (136px). The currency is not a column of its own: the
 * plan cell carries the ISO code under the price, which freed the 4.5rem it used to take.
 *
 * `plan` holds its price line and its billing-cycle line each on one line, in every language.
 * Measured on one line at 1280 (2026-10-09): a price line runs up to ~6.4px per character (ES
 * "15.000,00 CAD/año", 17 chars, 108px), a cycle label up to ~6.2px (ES "Facturación
 * mensual", 19 chars, 117px; FR "Facturation mensuelle", 21 chars, 125px). The widest the
 * cell must hold is the FR cycle label, 21 x 6.2 = ~130px + 24px = 154px, and a five-digit
 * FR price, "99 999,00 $ US/mois", 19 x 6.4 = ~122px + 24px = 146px. At 7rem (112px) the
 * price ran into the cell's edge and the cycle broke over two lines at 1280 in FR and ES, so
 * `plan` takes 10rem (160px).
 *
 * `actions` holds the row buttons (seven at most) in a 4-column grid on two rows:
 * 4 x 28px + 3 x 2px gaps + 16px of padding = 134px, inside 8.5rem = 136px.
 */
export const TENANT_COLUMNS = [
  { id: "name", headerKey: "tl.colCabinet", flex: true, minWidth: 8 },
  { id: "owner", headerKey: "tl.colOwner", flex: true, minWidth: 9 },
  { id: "plan", headerKey: "tl.colPlan", width: 10 },
  { id: "trial", headerKey: "tl.colTrial", width: 5.5 },
  { id: "status", headerKey: "tl.colStatus", width: 9.5 },
  { id: "actions", headerKey: "tl.colActions", width: 8.5 },
];

export const TENANT_TABLE_BUDGET_REM = 57.5;

/** Estimated text widths in px, see the arithmetic above; the layout test checks every label against them. */
export const STATUS_LABEL_PX_PER_CHAR = 5.7;
export const HEADER_PX_PER_CHAR = 8.2;
export const PRICE_PX_PER_CHAR = 6.4;
export const CYCLE_LABEL_PX_PER_CHAR = 6.2;
/** The largest plan price the plan cell is sized for: five digits (the catalogue's top is 15 000 CAD/yr). */
export const PLAN_PRICE_MAX_CENTS = 9_999_900;
export const STATUS_BADGE_CHROME_PX = 22; // 2 x 10px padding + 2px border
export const CELL_PADDING_PX = 24; // 2 x 12px (px-3)

/** The narrowest the table is laid out (fixed widths + flexible minimums); below it the wrapper scrolls instead of crushing columns. */
export const tenantTableWidthRem = () => TENANT_COLUMNS.reduce((sum, c) => sum + (c.flex ? c.minWidth : c.width), 0);
